import { AXES_MSG, ESTIMATE_PC_MSG } from "@genbaryoku/contract";
import { useEffect, useState, type ReactNode } from "react";
import { type BackNav } from "../../components/BackButton.jsx";
import { DocField, DocHead } from "../../components/DocHead.jsx";
import { PlaceSelect, TradeSelect, type AxesLists } from "../../components/EstimateAxes.jsx";
import { t } from "../../labels.js";
import { yen } from "../../lib/num.js";

/* ============================================================================
   見積書の頭（#131、Figma PC1 42:194 → 2回目は PC2 簡潔版 53:201）
   ① 上の帯：‹ 戻る／工事名＋状態／顧客名 御中・見積日・No.（2行目）／
     右に 合計（税込）を大きく・税抜と消費税を小さく／主のボタン。帯の下に開くもの（詳細・新しい案件）は children。
     #173：右の「≡」（PDF・見積依頼・削除）は題の右の「⋯」に入れた（見出しのメニューは1つ）
     #187（10/15 射場さん）：2行目の右は 表示順・元に戻す／やり直す・社内用・詳細（tools。前は表の上の道具の列）。
     担当者・支店・案件・現場は「詳細」の中へ（前は2行目）。2行目は 44px・部品 38px で、1280px でも1段（狭いときは顧客名を「…」で切る）
   紙（PDF）は変えない。ここは画面だけ
   ============================================================================ */

/** 見出しの右の状態チップ（#171、docs/10 §5.12）：発行済 MM/DD・下書き・取消済・受注・失注・訂正中 n回目（#172 で「第n版」から。画面の呼び方は回）。title は乗せたときの説明 */
export type TopStatus = { label: string; kind: "draft" | "issued" | "ordered" | "lost"; title?: string | undefined };

export function TopBand({
  back,
  title,
  help,
  status,
  client,
  date,
  docNo,
  total,
  subtotal,
  taxTotal,
  save,
  undo,
  primary,
  tools,
  chips,
  actions,
  menu,
  meta,
  children,
}: {
  back: BackNav;
  /** 工事名の欄（読み上げの名前は「工事名」） */
  title: ReactNode;
  help: ReactNode;
  status: TopStatus;
  /** 顧客名のボタン（押すと顧客を変える）と、その下の候補 */
  client: ReactNode;
  date: ReactNode;
  docNo: string | null;
  total: number;
  subtotal: number;
  taxTotal: number;
  /** 自動保存の状態（2行。#245） */
  save: ReactNode;
  /** 元に戻す／やり直す（#245：1段目） */
  undo?: ReactNode;
  primary: ReactNode;
  /** 3段目の右：表示順・紙の形・社内用・詳細（#187・#245） */
  tools?: ReactNode;
  /** 状態チップの右に足すチップ（分割 n／m回 請求済 など。#171） */
  chips?: ReactNode;
  /** 主なボタン（訂正版作成・請求書作成 など。HeadActions part="main"）。#245 から1段目の主ボタンの左 */
  actions?: ReactNode;
  /** 「⋯」（#243）：頭のいちばん右 */
  menu?: ReactNode;
  /** 3段目の左（No. の右）に足すもの（#213：社内用のパートナー「P 見本不動産 10%」・見積依頼 n件 ›・送付の記録） */
  meta?: ReactNode;
  /** 帯の下に開くもの（くわしく・新しい案件の欄） */
  children?: ReactNode;
}) {
  // #245（docs/94 B-4 案B）：帳票の頭（DocHead）の3段。testid は前の形（estimate-head・estimate-status・doc-no・estimate-head-tools・head-actions・head-dots）のまま
  return (
    <DocHead
      back={back}
      save={save}
      undo={undo}
      primary={
        <>
          {actions ? <span className="hd-acts" data-testid="head-actions">{actions}</span> : null}
          {primary}
        </>
      }
      menu={menu}
      kind="estimates"
      kindLabel="見積書"
      title={
        <>
          {title}
          <span className={"chip st " + status.kind} title={status.title} data-testid="estimate-status">{status.label}</span>
          {chips}
        </>
      }
      help={help}
      total={{ total, subtotal, taxTotal }}
      meta={
        <>
          <DocField label="宛先">{client}</DocField>
          <DocField label="見積日">{date}</DocField>
          <DocField label="No.">
            <span className="num" data-testid="doc-no">{docNo ?? ESTIMATE_PC_MSG.noUntilIssue}</span>
          </DocField>
          {meta}
        </>
      }
      tools={tools ? <span className="estools" data-testid="estimate-head-tools">{tools}</span> : null}
      testId="estimate-head"
    >
      {children}
    </DocHead>
  );
}

/** 版がずれたとき（409）：読み直すか。読み直すと、ほかで変えた分とこの画面で直した分の両方が残る */
export function StaleDialog({ busy, onReload, onLater }: { busy: boolean; onReload: () => void; onLater: () => void }) {
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && !busy && onLater()}>
      <div className="dlg" role="alertdialog" aria-label={ESTIMATE_PC_MSG.staleAsk} data-testid="stale-dialog" onKeyDown={(e) => e.key === "Escape" && !busy && onLater()}>
        <div className="dh">{ESTIMATE_PC_MSG.staleAsk}</div>
        <div className="db">
          <p style={{ margin: 0 }}>{ESTIMATE_PC_MSG.staleNote}</p>
        </div>
        <div className="df">
          <button type="button" className="btn quiet" disabled={busy} onClick={onLater}>{ESTIMATE_PC_MSG.staleLater}</button>
          <button type="button" className="btn primary" autoFocus disabled={busy} onClick={onReload} data-testid="stale-reload">{ESTIMATE_PC_MSG.staleReload}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * 「工種なし n行 → 入れる」「場所なし n行 → 入れる」の小さな窓（#131 の2回目）。該当の行を順に並べ、行ごとに選ぶ（Tab で次の行へ）。
 * 入れると並べ方の見出しの下に移る（並べ方の計算は contract の layoutLines＝画面と PDF が同じ）
 */
export function AxisFillDialog({ kind, rows, lists, onAskNew, onApply, onClose }: {
  kind: "trade" | "place";
  rows: Array<{ id: string; name: string }>;
  lists: AxesLists;
  onAskNew: (done: (placeId: string) => void) => void;
  onApply: (picked: Record<string, string>) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<Record<string, string>>({});
  // 言葉（{trade}＝工種／分類）は t() を通してから題に埋める（#188：前は「{trade}を入れる」がそのまま出た）
  const title = ESTIMATE_PC_MSG.fillTitle.replace("{axis}", t(kind === "place" ? AXES_MSG.place : AXES_MSG.trade));
  const n = Object.values(picked).filter(Boolean).length;
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  useEffect(() => {
    document.getElementById("fill_0")?.focus();
  }, []);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="dlg fill"
        role="dialog"
        aria-label={title}
        data-testid="fill-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          onApply(picked);
        }}
      >
        <div className="dh">{title}</div>
        <div className="db">
          <div className="filllist">
            {rows.map((r, k) => (
              <label className="fillrow" key={r.id} data-testid="fill-row">
                <span className="fn" title={r.name}>{r.name || t("（{lineName}なし）")}</span>
                {kind === "trade" ? (
                  <TradeSelect id={`fill_${k}`} value={picked[r.id] ?? null} trades={lists.trades} onChange={(v) => setPicked((x) => ({ ...x, [r.id]: v ?? "" }))} />
                ) : (
                  <PlaceSelect id={`fill_${k}`} value={picked[r.id] ?? null} lists={lists} onChange={(v) => setPicked((x) => ({ ...x, [r.id]: v ?? "" }))} onAskNew={onAskNew} />
                )}
              </label>
            ))}
          </div>
        </div>
        <div className="df">
          <button type="button" className="btn quiet" onClick={onClose}>取消</button>
          <button type="submit" className="btn primary" disabled={!n} data-testid="fill-apply">{ESTIMATE_PC_MSG.fillIn}{n ? `（${n}行）` : ""}</button>
        </div>
      </form>
    </div>
  );
}
